import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

// ---------- Attendance moving ticker ----------
// Pure CSS marquee — buttery on low-end machines, no JS animation frames.
// The track renders two copies of the message list back-to-back so the
// loop has no visible jump. Hover pauses; `prefers-reduced-motion` halts it.
//
// Messages are loaded from /api/settings.ticker_messages so super admins can
// edit them from Control Panel without a deploy. Falls back to safe defaults
// if the settings call fails or the array is empty.
//
// Used on both Attendance and Dashboard pages so the cadence reminder
// follows users wherever they look first.

const DEFAULT_MESSAGES = [
    { icon: "🗓", text: "Attendance instances are updated every month by the 3rd." },
    { icon: "ℹ", text: "Late entries, early exits, OFF and WFH days are rolled up monthly." },
];

export const AttendanceTicker = () => {
    const [messages, setMessages] = useState(DEFAULT_MESSAGES);
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/settings");
                if (!alive) return;
                const items = Array.isArray(r.data?.ticker_messages) ? r.data.ticker_messages : [];
                setMessages(items.length ? items : DEFAULT_MESSAGES);
                setEnabled(r.data?.ticker_enabled !== false);
            } catch {
                if (alive) {
                    setMessages(DEFAULT_MESSAGES);
                    setEnabled(true);
                }
            }
        })();
        return () => { alive = false; };
    }, []);

    if (!enabled || messages.length === 0) return null;

    return (
        <div
            data-testid="attendance-ticker"
            className="relative overflow-hidden rounded-lg border border-teal-200 bg-gradient-to-r from-teal-50 via-white to-teal-50"
            role="status"
            aria-live="polite"
        >
            <div className="absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-teal-50 to-transparent pointer-events-none" />
            <div className="absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-teal-50 to-transparent pointer-events-none" />
            <span className="absolute top-1/2 -translate-y-1/2 left-3 z-20 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-[#1F7F79] bg-white border border-teal-200 px-2 py-0.5 rounded-full">
                <span className="h-1.5 w-1.5 rounded-full bg-[#2EB4AC] animate-pulse" /> Live notice
            </span>
            <div className="attendance-ticker-track flex items-center gap-12 py-3 pl-32 pr-12 whitespace-nowrap text-sm text-slate-700">
                {[0, 1].map((dup) => (
                    <div key={dup} className="flex items-center gap-12 shrink-0">
                        {messages.map((m, i) => (
                            <span key={`${dup}-${i}`} className="inline-flex items-center gap-2">
                                <span aria-hidden className="text-base">{m.icon || "•"}</span>
                                <span className="font-medium">{m.text}</span>
                                <span aria-hidden className="text-slate-300 mx-2">·</span>
                            </span>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default AttendanceTicker;
